import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * The bits the three Notion* controls (select, date, time) share.
 *
 * They predate the editorial redesign: each one used to render as a borderless
 * inline button with its own padding and its own type size, which was fine when
 * every drawer field looked like that. It stopped being fine once the drawers
 * moved to `.input-field` boxes — a bordered Service input stacked on three
 * ghost controls that didn't share its box, its height or its left edge.
 *
 * So the shape is a prop now.
 */

/**
 * `field` matches `.input-field` exactly — same box, same padding, same type
 * size — so a picker can stand in a form column beside a text input without
 * the column visibly breaking. `inline` is the quiet form for a table cell or
 * a definition-list row, where a full box would read heavier than the text
 * around it.
 */
export type NotionVariant = 'field' | 'inline';

export function triggerClass(variant: NotionVariant, className = '') {
  return variant === 'field'
    ? `input-field flex w-full items-center justify-between gap-2 text-left ${className}`
    : `-mx-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[15px] text-primary transition-colors hover:bg-zinc-900/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${className}`;
}

/**
 * The inner content of a `field` trigger.
 *
 * `1.5em` is the line box `.input-field`'s 15px type produces, so a 20px Tag
 * and a line of text both come out at the same 44.5px as the real `<input>`
 * sitting next to them — a half-pixel off is visible where two controls share
 * a grid row.
 */
export const TRIGGER_CONTENT = 'flex min-h-[1.5em] items-center';

/** Popover chrome, shared by the select menu and the two picker popups. */
export const POPOVER =
  'overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-[0_12px_32px_rgba(30,34,40,0.12)]';

function scrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Whether a popover of roughly `height` should open upward.
 *
 * These controls live near the bottom of a drawer whose body is its own scroll
 * container, so a menu that opens downward gets clipped by the panel rather
 * than by the viewport — the Status select on a booking was losing half its
 * options. Measure against the scroll container when there is one.
 */
export function useDropUp(
  isOpen: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  height: number
) {
  const [dropUp, setDropUp] = useState(false);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!isOpen || !anchor) return;

    const rect = anchor.getBoundingClientRect();
    const scroller = scrollParent(anchor);
    const bounds = scroller?.getBoundingClientRect();
    const floor = bounds ? Math.min(bounds.bottom, window.innerHeight) : window.innerHeight;
    const ceiling = bounds ? Math.max(bounds.top, 0) : 0;

    setDropUp(rect.bottom + height > floor && rect.top - height > ceiling);
  }, [isOpen, anchorRef, height]);

  return dropUp;
}
